import {filter, findLast} from 'lodash';

import {TicketingConversationModel} from '../../../../../libs/inboxes/business/src/ticketing/conversation';
import {getIsConversationTicketingEnabled} from '../../../../../libs/inboxes/store/src/ticketing/conversationsTicketingSelectors';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {ConversationTeamStatusesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TicketStatusModel} from '../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  ActivityModel,
  BasicActivityModel,
  BasicActivityTagModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
  RBasicActivity,
  RBasicActivityReminder,
  RBasicActivityTag,
} from '../../../../../src/models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../src/models/api/activitySourceModel';
import {ConversationBaseModel} from '../../../../../src/models/api/conversationModel';
import {ReminderModel} from '../../../../../src/models/api/reminderModel';
import {getFindTeammate, getUser} from '../../../../../src/store/data/teammates/teammatesSelectors';
import {getLocalize} from '../../../../../src/store/stateless/internationalizationSelectors';
import {AppParametricSelector, createSelector} from '../../../../helpers/reselectHelpers';
import {
  computeStaticViewerActivityType,
  StaticViewerActivity,
  StaticViewerActivityDetails,
  StaticViewerActivityTypesEnum,
} from './staticViewerHelpers';

/*
 * Constants.
 */

const conversationStatusEligibleStaticViewerBannerActivityTypes: Record<
  ConversationTeamStatusesEnum,
  ReadonlyArray<BasicActivityModel['type']>
> = {
  [ConversationTeamStatusesEnum.OPEN]: [BasicActivityTypesEnum.REOPEN],
  [ConversationTeamStatusesEnum.ARCHIVED]: [BasicActivityTypesEnum.ARCHIVE],
  [ConversationTeamStatusesEnum.TRASHED]: [BasicActivityTypesEnum.TRASH],
  [ConversationTeamStatusesEnum.SPAM]: [],
  [ConversationTeamStatusesEnum.DESTROYED]: [],
  [ConversationTeamStatusesEnum.INVISIBLE]: [],
};

/*
 * Selectors.
 */

function makeGetTeamReminderForStaticViewerActivity<P>(
  getConversation: AppParametricSelector<P, ConversationBaseModel>,
): AppParametricSelector<P, ReminderModel | undefined> {
  return createSelector([getConversation], (conversation) =>
    conversation.reminders.find((r) => r.isTeamReminder),
  );
}

export function makeGetStaticViewerActivity<P extends {conversation: TicketingConversationModel}>(
  getPageActivities: AppParametricSelector<P, ReadonlyArray<ActivityModel>>,
  getConversation: AppParametricSelector<P, ConversationBaseModel>,
  getTicketStatus: AppParametricSelector<P, TicketStatusModel | undefined>,
): AppParametricSelector<P, StaticViewerActivity | undefined> {
  return createSelector(
    [
      getPageActivities,
      getConversation,
      getTicketStatus,
      makeGetTeamReminderForStaticViewerActivity(getConversation),
      getIsConversationTicketingEnabled,
    ],
    (
      activities,
      conversation,
      ticketStatus,
      teamReminder,
      isConversationTicketingEnabled,
    ): StaticViewerActivity | undefined => {
      const staticViewerType = computeStaticViewerActivityType(
        conversation,
        ticketStatus,
        teamReminder,
        isConversationTicketingEnabled,
      );
      if (!staticViewerType) {
        return undefined;
      }

      switch (staticViewerType) {
        case StaticViewerActivityTypesEnum.TICKET_STATUS: {
          const activity = findLast(filter(activities, RBasicActivityTag.is), (a) =>
            isEligibileTicketStatusActivity(a, ticketStatus),
          );

          return {type: staticViewerType, activity};
        }
        case StaticViewerActivityTypesEnum.REMINDER: {
          const activity = findLast(filter(activities, RBasicActivityReminder.is), (a) =>
            isEligibileReminderActivity(a, teamReminder),
          );

          return {type: staticViewerType, activity};
        }
        case StaticViewerActivityTypesEnum.OPEN:
        case StaticViewerActivityTypesEnum.ARCHIVED:
        case StaticViewerActivityTypesEnum.TRASHED: {
          const activity = findLast(filter(activities, RBasicActivity.is), (a) =>
            conversationStatusEligibleStaticViewerBannerActivityTypes[conversation.teamStatus].includes(
              a.type,
            ),
          );

          return {type: staticViewerType, activity};
        }
        default:
          throw new UnreachableError(staticViewerType);
      }
    },
  );
}

export function makeGetStaticViewerActivityDetails<P>(
  getStaticViewerActivity: AppParametricSelector<P, StaticViewerActivity>,
): AppParametricSelector<P, StaticViewerActivityDetails> {
  return createSelector(
    [getLocalize, getFindTeammate, getStaticViewerActivity, getUser],
    (localize, findTeammate, staticViewerActivity, user) => {
      // Teammate Name
      const {activity} = staticViewerActivity;

      const activityDetails: StaticViewerActivityDetails = {
        teammateName: undefined,
        date: activity?.date,
      };

      if (activity?.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE) {
        const teammate = findTeammate(activity.source.teammateId);
        if (teammate) {
          activityDetails.teammateName =
            user.id === teammate.id
              ? localize(
                  defineMessage({
                    id: 'activity-static-body-you"',
                    message: 'you',
                  }),
                )
              : buildTeammateDisplayName(teammate);
        }
      }

      return activityDetails;
    },
  );
}

/*
 * Helpers.
 */

// Filter reminder activities for ones that are associated with a team reminder
function isEligibileReminderActivity(
  activity: BasicActivityModel,
  teamReminder: ReminderModel | undefined,
): boolean {
  // If the activity is not a reminder activity, nothing to do
  if (
    activity.basicVisualType !== BasicVisualTypesEnum.REMINDER ||
    activity.source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE
  ) {
    return false;
  }

  return activity.source.teammateId === teamReminder?.teammateId;
}

// Filter ticket status change activities
function isEligibileTicketStatusActivity(
  activity: BasicActivityTagModel,
  ticketStatus: TicketStatusModel | undefined,
): boolean {
  // Ignore tag activities that are not ticket status updates.
  if (activity.type !== BasicActivityTypesEnum.TICKET_STATUS_UPDATE) {
    return false;
  }

  /*
   * TODO: Currently, once a conversation receives a ticket status tag the BE maintains it
   * and keeps it in sync with the conversation status even after the conversation is moved to a non-ticketing
   * inbox or if the inbox disables ticketing. A side-effect of this is that the FE filters out
   * reopen and archive activities when a conversation has ticket status activities to remove redundant information.
   * This means that once a conversation has a ticket status, there are no longer reopen/archive activities
   * to rely on to determine if the conversation is eligible to be a static viewer banner so must by depend on
   * the Open/Resolved status. The static viewer banner will be responsible for rendering the correct UI.
   *
   * Once the backend is updated to remove the ticket status tag when the conversation is no longer in a ticketing
   * inbox or if the inbox disables ticketing, we should update this function to check if the conversation is
   * ticketing enabled.
   */

  // If the conversation is ticketing enabled and has a ticket status, it is eligible to be a static viewer banner
  return activity.tagId === ticketStatus?.tagId;
}
